import { Button, Stack, Typography } from "@mui/joy";
import { useCallback, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Status } from "./workflow/CoachWorkflow";
import { PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT } from "api/patientApi";
import {
  EXPIRATION_NOTICE_DETAIL_ENDPOINT,
  EXPIRATION_NOTICE_DOMAIN,
  ExpirationDetailType,
  expirationNoticeApi,
} from "api/expirationNoticeApi";
import { theme } from "styles/theme";

function ExpirationNoticeRequired({
  expirationDate,
  expiryDaysLeft,
}: {
  expirationDate: string;
  expiryDaysLeft: number;
}) {
  const queryClient = useQueryClient();
  const [patientState, setPatientState] = useRecoilState(patientAtom);

  const formattedDate = new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
  }).format(new Date(expirationDate));

  const sendQueryFn = useCallback(
    () => expirationNoticeApi.send(`${patientState?.patientId}`),
    [patientState?.patientId]
  );

  const { mutate: send, isLoading } = useMutation(sendQueryFn, {
    onSuccess: () => {
      setPatientState((patientState) =>
        patientState
          ? { ...patientState, isExpirationNoticeRequired: false }
          : patientState
      );
      queryClient.invalidateQueries([PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT]);
    },
  });

  const skipQueryFn = useCallback(
    () => expirationNoticeApi.skip(`${patientState?.patientId}`),
    [patientState?.patientId]
  );

  const { mutate: skip } = useMutation(skipQueryFn, {
    onSuccess: () => {
      setPatientState((patientState) =>
        patientState
          ? { ...patientState, isExpirationNoticeRequired: false }
          : patientState
      );
      queryClient.invalidateQueries([PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT]);
    },
  });

  return (
    <Stack direction={"column"} spacing={1}>
      <Stack direction={"row"} spacing={"12px"} alignItems="start">
        <Status status={"human_check_required"} />
        <Stack direction={"column"} sx={{ flex: 1 }}>
          <Typography>{"만료 예정"}</Typography>
          <Typography
            variant={"Footnote"}
            sx={{
              color: theme.vars.palette.Content.Weak,
              fontWeight: 500,
            }}
          >{`${formattedDate} (${expiryDaysLeft}일 남음)`}</Typography>
        </Stack>
      </Stack>
      <Stack sx={{ width: "100%" }} direction={"row"} spacing={"4px"}>
        <Button
          size={"xs"}
          variant="Outlined"
          fullWidth
          sx={{ px: "0", flex: 1 }}
          onClick={() => skip()}
        >
          {"생략하기"}
        </Button>

        <Button
          size={"xs"}
          variant={"Outlined"}
          disabled={isLoading}
          fullWidth
          sx={{ px: "0", flex: 1 }}
          onClick={() => send()}
        >
          {"안내하기"}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function ExpirationNoticeSection() {
  const patientState = useRecoilValue(patientAtom);

  const [expirationNoticeDetail, setExpirationNoticeDetail] = useState<
    ExpirationDetailType | undefined
  >(undefined);

  const detailQueryFn = useCallback(
    () => expirationNoticeApi.detail(`${patientState?.patientId}`),
    [patientState?.patientId]
  );
  const { refetch: getDetail } = useQuery(
    [
      EXPIRATION_NOTICE_DOMAIN,
      EXPIRATION_NOTICE_DETAIL_ENDPOINT,
      patientState?.patientId,
    ],
    detailQueryFn,
    {
      onSuccess: (data) => {
        if (data && data.result) {
          const detail = data.result;
          setExpirationNoticeDetail(detail);
        }
      },
    }
  );

  return patientState?.isExpirationNoticeRequired && expirationNoticeDetail ? (
    <Stack direction={"column"} spacing={1} sx={{ py: "12px" }}>
      <ExpirationNoticeRequired
        expirationDate={expirationNoticeDetail.expirationDate}
        expiryDaysLeft={expirationNoticeDetail.expiryDaysLeft}
      />
    </Stack>
  ) : (
    <></>
  );
}
